import { Injectable } from '@angular/core';

interface CacheEntry {
  value: string;
  savedAt: number;
}

@Injectable({ providedIn: 'root' })
export class ResourceCacheService {
  private readonly ttlMs = 3 * 60 * 1000; // 3 minutes
  private urlCache = new Map<string, CacheEntry>();     // 汎用URL（音声/画像など）
  private csvCache = new Map<string, CacheEntry>();     // CSVテキスト

  /**
   * 音声・画像など、URLをHEADで検証してTTL内キャッシュを返す
   */
  async resolveUrl(originalUrl: string): Promise<string> {
    if (!originalUrl) {
      throw new Error('リソースが見つかりません');
    }
    const url = new URL(originalUrl);
    const base = url.origin + url.pathname;
    const now = Date.now();
    const cached = this.urlCache.get(base);

    if (cached && now - cached.savedAt < this.ttlMs) {
      return cached.value;
    }

    // HEADを行わず、そのままURLを保存（SAS期限付きでもキャッシュキーはパスでまとめる）
    this.urlCache.set(base, { value: originalUrl, savedAt: now });
    return originalUrl;
  }

  /**
   * CSV をGETで取得しテキストを返す（TTL内ならキャッシュ）
   */
  async fetchCsvText(originalUrl: string): Promise<string> {
    if (!originalUrl) {
      throw new Error('CSVが見つかりません');
    }
    const resolvedUrl = await this.resolveUrl(originalUrl);
    const url = new URL(resolvedUrl);
    const base = url.origin + url.pathname;
    const now = Date.now();
    const cached = this.csvCache.get(base);
    if (cached && now - cached.savedAt < this.ttlMs) {
      return cached.value;
    }

    const text = await fetch(resolvedUrl).then((r) => {
      if (!r.ok) {
        const error = new Error(
          `CSVを取得できませんでした (status: ${r.status})`,
        ) as Error & { status: number };
        error.status = r.status;
        throw error;
      }
      return r.text();
    });

    this.csvCache.set(base, { value: text, savedAt: now });
    return text;
  }
}
